// Slice 3B.2 — pure helpers that drive the WHERE view-mode toggle. Given the
// resolver's `whereTree` (Slice 3B.1), decide whether the clause shows as the
// editable Inline pills or the read-only Rail, and key the per-clause pinned
// choice. No React, no store imports beyond the WhereViewMode union type.

import type { BooleanNode } from '../../../../sql-resolver/types';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { WhereViewMode } from '../../store/vqeStore';

/**
 * Per-(document, statement, clause) key for the pinned view mode. `kind`
 * (default `'where'`) keys WHERE and HAVING independently — pinning one
 * clause's rail/inline view never flips the other's (Plan 3 decision 2).
 */
export function whereViewKey(
  documentId: string,
  statementId: string,
  kind: BooleanClauseTarget = 'where',
): string {
  return `${documentId}:${statementId}:${kind}`;
}

// Max explicit-group nesting along any path. Leaves contribute 0; an explicit
// (source-parenthesised) group contributes 1 + its deepest child; an implicit
// precedence group contributes only its deepest child (no +1).
function explicitNesting(node: BooleanNode): number {
  if (node.kind === 'leaf') return 0;
  let deepest = 0;
  for (const child of node.children) deepest = Math.max(deepest, explicitNesting(child));
  return (node.explicit ? 1 : 0) + deepest;
}

/** Visible nesting depth: root group counts as 1; each explicit level adds 1. */
export function whereViewDepth(root: BooleanNode): number {
  return 1 + explicitNesting(root);
}

/** Total number of leaf predicates in the tree. */
export function whereLeafCount(root: BooleanNode): number {
  if (root.kind === 'leaf') return 1;
  return root.children.reduce((sum, child) => sum + whereLeafCount(child), 0);
}

/**
 * Auto rule (mockup two-mode-toggle.html): Inline when depth ≤ 1 AND ≤ 4
 * conditions; Rail when depth ≥ 2 OR > 4 conditions.
 */
export function resolveAutoWhereView(root: BooleanNode): 'inline' | 'rail' {
  return whereViewDepth(root) <= 1 && whereLeafCount(root) <= 4 ? 'inline' : 'rail';
}

/** Resolve the pinned mode to a concrete view; 'auto' defers to the heuristic. */
export function effectiveWhereView(
  pinned: WhereViewMode,
  root: BooleanNode,
): 'inline' | 'rail' {
  return pinned === 'auto' ? resolveAutoWhereView(root) : pinned;
}
